import React, { useContext, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'

import { mediaQueryUp } from 'components/utils/breakpoint'
import ProductTypeLabel from 'components/Luxkit/Label/ProductTypeLabel'
import config from 'constants/config'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import OfferTileLoggedInButtons from '../../Support/OfferTileLoggedInButtons'
import TileInclusions from '../../Support/OfferTileInclusions'
import SoldOutMessage from '../../Support/SoldOutMessage'
import Subtitle from 'components/Luxkit/Typography/Subtitle'
import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import ImageCarousel from 'components/Luxkit/Carousel/ImageCarousel'
import { connect } from 'react-redux'
import { nonNullable, take } from 'lib/arrayUtils'
import TextButton from 'components/Luxkit/Button/TextButton'
import useShowNewHotelSearch from 'components/SearchV2/Hooks/useShowNewHotelSearch'
import BundleOfferTilePricing from '../../Support/BundleOfferTilePricing'
import BundleOfferLocation from '../../BundleOfferLocation'
import OfferTileLocationSection from '../OfferTileLocationSection'
import Group from 'components/utils/Group'
import { Main, MainImageSegment, OfferOverlayContainer, StyledOfferBadge, DetailSegment } from './SearchTileStyles'

const PropertyDetails = styled.div`
  > * + * {
    margin-top: ${rem(4)};
  }
`

const PriceDetails = styled.div`
  align-self: flex-end;
  display: flex;
  flex-direction: column;
  margin-top: ${rem(16)};
  position: relative;
  width: auto;
  min-height: ${rem(84)};
  
  ${mediaQueryUp.md} {
    margin-left: ${rem(20)};
  }
  &.landing-page-test {
    margin-top: 0;
  }
  > * + * {
    margin-top: ${rem(8)};
  }
`

const PriceWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-content: flex-end;
  flex-direction: column;
  min-width: ${rem(180)};
  align-items: flex-start;
`

const AlignedLoader = styled(LoadingIndicator)`
  align-items: flex-start;
`

interface Props {
  offer: App.OfferBundleAndSave;
  passedWalledGarden: boolean;
  productClick?: (offer: App.OfferBundleAndSave) => void;
  filters?: App.OfferListFilters;
  soldOut: boolean;
  bestPricePackage: App.BundlePackageWithPrice;
  eagerLoadFirstImage?: boolean;
  offerUrl: string;
  iOS: boolean;
}

function NewSearchBundleOfferTileExpanded(props: Props) {
  const {
    offer,
    passedWalledGarden,
    filters,
    soldOut,
    bestPricePackage,
    eagerLoadFirstImage,
    offerUrl,
    iOS,
  } = props

  const {
    name,
    tileDescription,
    tileInclusions,
    tileInclusionsHeading,
  } = offer

  const showBadge = !!offer.badge && config.BRAND === 'luxuryescapes'

  const inclusions = tileDescription ?? offer.inclusions
  const inclusionsList = tileInclusions
  const inclusionsHeading = tileInclusionsHeading || 'Your handpicked inclusions:'

  const { searchTargetLabel, offerDistanceFromSearchTarget } = useContext(GlobalSearchStateContext)
  const searchTargetDistance = offerDistanceFromSearchTarget?.[offer.id]
  const showSearchDistance = !!searchTargetLabel && typeof searchTargetDistance === 'number' && searchTargetDistance > 0

  // Eg. `Melbourne`, `Victoria` & `Kuta`, `Bali`
  const locationCaption = useMemo(() =>
    Object.values(offer.bundledOffers ?? {}).map(offer =>
      nonNullable([
        offer.locationHeading,
        offer.locationSubheading,
      ]).join(', '))
      .join(' & ')
  , [offer.bundledOffers])

  // This is temporary until we can improve memory on the offer listing page
  const images = iOS ? take(offer.images, 1, 0) : offer.images

  const showNewTile = useShowNewHotelSearch()

  return (
    <Main>
      <MainImageSegment>
        <ImageCarousel
          images={images}
          eagerLoadFirstImage={eagerLoadFirstImage}
        />
        <OfferOverlayContainer>
          {!showNewTile && <div>
            <ProductTypeLabel productType={offer.productType} />
          </div>}
        </OfferOverlayContainer>
        {showBadge && offer.badge && <StyledOfferBadge badge={offer.badge} dimensions={showNewTile ? { width: 70, height: 70 } : undefined} />}
      </MainImageSegment>
      <div>
        <DetailSegment>
          <PropertyDetails>
            <Group direction="vertical" fullWidth gap={4}>
              <OfferTileLocationSection
                location={locationCaption}
                showSearchDistance={showSearchDistance}
                shouldShowMapLink={false}
                mapSearchUrl=""
                searchTargetDistance={searchTargetDistance}
                searchTargetLabel={searchTargetLabel}
              />
            </Group>

            {showNewTile && <div>
              <ProductTypeLabel productType={offer.productType} />
            </div>}

            <BundleOfferLocation offer={offer} />

            <Subtitle variant="subtitle3">{name}</Subtitle>

            {(inclusions || (inclusionsList && inclusionsList?.length > 0)) &&
              <TileInclusions
                offerTitle={offer.property?.name ? offer.property.name : offer.name}
                offerURL={offerUrl}
                content={inclusions}
                tileInclusionsList={inclusionsList}
                heading={inclusionsHeading}
              />
            }
          </PropertyDetails>
          <PriceDetails>
            <PriceWrapper>
              <Group
                fullWidth
                direction="vertical"
                verticalAlign="start"
                horizontalAlign="start"
                gap={16}
              >
                {bestPricePackage &&
                  <OfferTileLoggedInButtons
                    signUpText="Sign up for free"
                    discountPercentage={bestPricePackage?.discountPercent ? Math.round(bestPricePackage.discountPercent) : undefined}
                    align="right"
                  >
                    <BundleOfferTilePricing
                      offer={offer}
                      pkg={bestPricePackage}
                      offerUrl={offerUrl}
                      rooms={filters?.rooms}
                      align="left"
                    />
                    <TextButton fit="full-width" kind="primary">
                      View offer
                    </TextButton>
                  </OfferTileLoggedInButtons>
                }
              </Group>
            </PriceWrapper>
            {soldOut && passedWalledGarden && <SoldOutMessage offer={offer} filters={filters} />}
            {!bestPricePackage && passedWalledGarden && <AlignedLoader floating opaque />}
          </PriceDetails>
        </DetailSegment>
      </div>
    </Main>
  )
}

NewSearchBundleOfferTileExpanded.defaultProps = {
  filters: {},
}

const mapStateToProps = (state: App.State) => ({
  iOS: state.config.userAgent.osName === 'iOS',
})

export default connect(mapStateToProps)(NewSearchBundleOfferTileExpanded)
